import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/BiosampleComponent.css";
import { API_BASE_URL } from "../../config"; 
import { FaArrowLeft } from 'react-icons/fa';

function BioSampleList() {
  const [bioSamples, setBioSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkedItems, setCheckedItems] = useState({});
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ package: "", status: "" });
  const pageSize = 10;
  const [selectedSample, setSelectedSample] = useState(null);
  const [selectedSRA, setSelectedSRA] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const extractPageNumber = (url) => {
    try {
      const params = new URL(url).searchParams;
      return parseInt(params.get("page"), 10);
    } catch {
      return null;
    }
  };

  const buildUrl = useCallback(
    (pageNum = 1) => {
      let url = `http://197.156.76.122/api/biosample/?page=${pageNum}`;
      if (search) url += `&search=${search}`;
      if (filters.package) url += `&package=${filters.package}`;
      if (filters.status) url += `&status=${filters.status}`;
      return url;
    },
    [search, filters]
  );

  const fetchData = useCallback((url) => {
    setLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setBioSamples(data.results || []);
        setNextUrl(data.next);
        setPrevUrl(data.previous);
        setTotalCount(data.count || 0);
        const newPage = extractPageNumber(url);
        if (newPage) setPage(newPage);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData(buildUrl(1));
  }, [search, filters, buildUrl, fetchData]);

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    if (pathParts[1] === "BioSample" && pathParts[2]) {
      const accession = pathParts[2];
      fetch(`${API_BASE_URL}/biosample/?search=${accession}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.results.length > 0) setSelectedSample(data.results[0]);
        });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) {
      const accession = location.hash.replace("#", "");
      setSelectedSRA({ experiment_accession: accession });
    }
  }, [location.hash]);

  const goToFirstPage = () => fetchData(buildUrl(1));
  const goToLastPage = () => fetchData(buildUrl(Math.ceil(totalCount / pageSize)));
  const goToNextPage = () => nextUrl && fetchData(nextUrl);
  const goToPrevPage = () => prevUrl && fetchData(prevUrl);

  const toggleCheck = (id) =>
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleFilterChange = (e) =>
    setFilters({ ...filters, [e.target.name]: e.target.value });

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(startItem + pageSize - 1, totalCount);

  const closeModal = () => {
    setSelectedSample(null);
    setSelectedSRA(null);
    navigate("/biosample", { replace: true });
  };

  const copyDetails = () => {
    if (!selectedSample) return;
    const filtered = Object.fromEntries(
      Object.entries(selectedSample).filter(
        ([key, value]) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          !["id", "created_at", "updated_at"].includes(key)
      )
    );
    navigator.clipboard.writeText(JSON.stringify(filtered, null, 2));
    alert("Copied!");
  };

  const openBioSample = (bs) => {
    setSelectedSample(bs);
    navigate(`/BioSample/${bs.accession}`, { replace: false });
  };

  const openSRAModal = (sra) => {
    setSelectedSRA(sra);
    navigate(`/sra#${sra.experiment_accession}`);
  };

  const PaginationControls = () => (
    <div className="pagination">
    {/* 🔙 Back to Home Button */}
      
        <button className="pci-back-home" onClick={() => navigate('/')}>
          <FaArrowLeft className="pci-back-icon" /> Back to Home
        </button>
      <button disabled={page <= 1} onClick={goToFirstPage}>
        ⏮ First
      </button>
      <button disabled={!prevUrl} onClick={goToPrevPage}>
        ⬅ Prev
      </button>
      <span className="page-label">
        Page {page} of {Math.ceil(totalCount / pageSize)}
      </span>
      <button disabled={!nextUrl} onClick={goToNextPage}>
        Next ➡
      </button>
      <button
        disabled={page >= Math.ceil(totalCount / pageSize)}
        onClick={goToLastPage}
      >
        Last ⏭
      </button>
    </div>
  );

  if (loading) return <div className="loading">Loading BioSamples...</div>;

  return (
    <div
      className={`biosample-container ${
        selectedSample || selectedSRA ? "modal-active" : ""
      }`}
    >
      {/* HEADER */}
      {!selectedSample && !selectedSRA && (
        <>
          <div className="biosample-header">
            <h2>Biosamples List ({totalCount})</h2> 
            
          </div>

          <div className="search-bar">
            <input
              type="text"
              placeholder="🔍 Search BioSamples..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="content-layout">
            <div className="results">
              <PaginationControls />
              <h4 className="header-summary">
                Items: {startItem} – {endItem} of {totalCount}
              </h4>

              {bioSamples.length === 0 ? (
                <div className="empty">No BioSamples found.</div>
              ) : (
                bioSamples.map((bs, idx) => (
                  <div className="biosample-item" key={bs.id}>
                    <div className="select-item">
                      <input
                        type="checkbox"
                        checked={!!checkedItems[bs.id]}
                        onChange={() => toggleCheck(bs.id)}
                      />
                      <span
                        className="title-link"
                        onClick={() => openBioSample(bs)}
                      >
                        {bs.title || bs.sample_name}
                      </span>
                    </div>
                    <div className="item-number">
                      {idx + 1 + (page - 1) * pageSize}.{" "}
                      <span
                        className="accession-link"
                        onClick={() => openBioSample(bs)}
                      >
                        <b>Accession:</b> {bs.accession}{" "}
                        {bs.internal_id && (
                          <>
                            {" "}
                            <b>| ID:</b> {bs.internal_id}
                          </>
                        )}
                      </span>
                    </div>
                    {bs.organism && (
                      <div className="section organism">Organism: {bs.organism}</div>
                    )}
                    {bs.bio_project && bs.bio_project.bio_project_accession && (
                      <div className="section bioproject">
                        BioProject:{" "}
                        <span
                          className="internal-link"
                          onClick={() =>
                            navigate(
                              `/bioproject/${bs.bio_project.bio_project_accession}`
                            )
                          }
                        >
                          {bs.bio_project.bio_project_accession}
                        </span>
                      </div>
                    )}
                    {bs.sra_id && (
                      <div className="section sra">
                        SRA:{" "}
                        <span
                          className="internal-link"
                          onClick={() => navigate(`/sra/${bs.sra_id}`)}
                        >
                          {bs.sra_id}
                        </span>
                      </div>
                    )}
                    {bs.package && <div className="section package">Package: {bs.package}</div>}
                    {bs.owner_name && (
                      <div className="section owner">Owner: {bs.owner_name}</div>
                    )}
                    <hr />
                  </div>
                ))
              )}
              <PaginationControls />
            </div>
          </div>
        </>
      )}

      {/* SAMPLE MODAL */}
      {selectedSample && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✖</button>
            <h2 className="modal-title">{selectedSample.title || selectedSample.sample_name}</h2>

            <div className="modal-buttons top-buttons">
              <button onClick={copyDetails}>📋 Copy</button>
              <button className="modal-back" onClick={closeModal}>⬅ Back</button>
            </div>

            <div className="ncbi-links-box">
              <h3>🔗 Related Links</h3>
              <ul>
                <li>
                  <a
                    href={`https://www.ncbi.nlm.nih.gov/biosample/${selectedSample.accession}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    BioSample @ NCBI
                  </a>
                </li>
                {selectedSample.sra_id && (
                  <li>
                    <a
                      href={`https://www.ncbi.nlm.nih.gov/sra/${selectedSample.sra_id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      SRA @ NCBI
                    </a>
                  </li>
                )}
                <li>
                  <a
                    href={`https://eutils.ncbi.nlm.nih.gov/entrez/eutils/efetch.fcgi?db=biosample&id=${selectedSample.accession}&retmode=xml`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    XML View
                  </a>
                </li>
              </ul>
            </div>

            <div className="modal-body">
              <div className="details-grid">
                {Object.entries(selectedSample)
                  .filter(
                    ([key, value]) =>
                      value !== "" &&
                      value !== null &&
                      !["id", "created_at", "updated_at"].includes(key)
                  )
                  .map(([key, value]) => (
                    <div className="detail-row" key={key}>
                      <div className="detail-label">{key.replace(/_/g, " ")}:</div>
                      <div className="detail-value">
                        {Array.isArray(value) ? value.join(", ") : String(value)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SRA MODAL */}
      {selectedSRA && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✖</button>
            <h2 className="modal-title">SRA Experiment: {selectedSRA.experiment_accession}</h2>
            <div className="modal-links">
              <a
                href={`https://www.ncbi.nlm.nih.gov/sra/${selectedSRA.experiment_accession}`}
                target="_blank"
                rel="noreferrer"
              >
                View on NCBI
              </a>
              <a
                href={`https://trace.ncbi.nlm.nih.gov/Traces/sra/?run=${selectedSRA.experiment_accession}`}
                target="_blank"
                rel="noreferrer"
              >
                Run Selector
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BioSampleList;

